import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, UserPlusIcon, LinkIcon, HistoryIcon, SendIcon, ZapIcon, CheckCircle2Icon, CopyIcon } from 'lucide-react';
import { useRaastTheme } from './RaastShared';
interface RaastHomeProps {
  onNavigate: (view: string) => void;
  onClose: () => void;
}
export function RaastHome({
  onNavigate,
  onClose
}: RaastHomeProps) {
  const {
    bgStyle,
    cardBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const options = [{
    id: 'register',
    title: 'Register RAAST ID',
    desc: 'Link your mobile number',
    icon: UserPlusIcon,
    view: 'register'
  }, {
    id: 'relink',
    title: 'Relink/Delink',
    desc: 'Manage your existing IDs',
    icon: LinkIcon,
    view: 'relink'
  }, {
    id: 'history',
    title: 'Merchant History',
    desc: 'View your RAAST merchant payments',
    icon: HistoryIcon,
    view: 'history'
  }, {
    id: 'requests',
    title: 'Payment Requests',
    desc: 'View and manage RTP requests',
    icon: CheckCircle2Icon,
    view: 'requests'
  }, {
    id: 'transfer',
    title: 'Pay to Merchant / Transfer',
    desc: 'Scan a QR or enter a till number',
    icon: SendIcon,
    // Opens the camera-first QR / Till payment flow
    view: 'qr_till'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2343" data-mp="3srm717qee6km">
      {/* Header — 16px regular, no Home icon here since this screen is Home itself */}
      <div className="px-6 pt-6 pb-4 flex items-center" data-id="element-2344" data-mp="3nrq65hczyv9a">
        <button onClick={onClose} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2345" data-mp="3nucdbfvz78qz">
          <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2346" data-mp="1lzliii2j6k4d" />
        </button>
        <h1 className={`text-[16px] font-normal ml-3 ${textPri}`} data-id="element-2347" data-mp="2uyml85hccorw">RAAST</h1>
      </div>

      <div className="px-6 flex-1 flex flex-col gap-4 pb-8 mt-1" data-id="element-2348" data-mp="2pygxv1i3tofm">
        {/* Your RAAST ID details — shown first */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 24
      }} className={`rounded-lg p-5 border shadow-sm ${cardBg} ${borderClr}`} data-id="element-2349" data-mp="2lxy9xijc12ip">
          <div className="flex items-start justify-between mb-4" data-id="element-2350" data-mp="34uzcyxtbssk0">
            <div className="flex items-center gap-3" data-id="element-2351" data-mp="2zwxykq6uhhev">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${isDark || isPlatinum ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2352" data-mp="2zwen9sbp9r9t">
                <ZapIcon className="w-6 h-6 text-[#F37024]" fill="currentColor" data-id="element-2353" data-mp="3sktq7ghk4j4s" />
              </div>
              <div data-id="element-2354" data-mp="8qf9aems4qtc">
                <p className={`text-[11px] font-normal uppercase tracking-wider ${textSec}`} data-id="element-2355" data-mp="8pngfbkkh76r">
                  Your RAAST ID
                </p>
                <p className={`text-[18px] font-bold mt-0.5 ${textPri}`} data-id="element-2356" data-mp="hqvf3uqzwa1d">
                  0300 1234567
                </p>
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#22C55E]/10 text-[#22C55E] flex items-center gap-1" data-id="element-2357" data-mp="mpy98k5fsvuk">
              <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-2358" data-mp="wg9oh2ukmrgr" />
              Active
            </span>
          </div>

          <div className={`h-px w-full ${borderClr} mb-4`} data-id="element-2359" data-mp="11fm8lfu25kfn" />

          <div className="space-y-3" data-id="element-2360" data-mp="11h72d38jszcg">
            <div className="flex items-center justify-between" data-id="element-2361" data-mp="15h7svm8x90zv">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2362" data-mp="15ev1yjl6rgao">Account Title</span>
              <span className={`text-[13px] font-medium ${textPri}`} data-id="element-2363" data-mp="1adpwgisimtam">
                KASHISH AHMED
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-2364" data-mp="ss05xkemfv08">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2365" data-mp="spdwtgc317fw">Linked Account</span>
              <span className={`text-[13px] font-medium ${textPri}`} data-id="element-2366" data-mp="16i3q85txxen1">
                Current Account ••••8910
              </span>
            </div>
            <div className="flex items-center justify-between gap-3" data-id="element-2367" data-mp="dtocdmmjp2da">
              <span className={`text-[13px] font-medium ${textSec} shrink-0`} data-id="element-2368" data-mp="9sm8gshoo24z">IBAN</span>
              <span className={`text-[13px] font-medium truncate ${textPri} flex items-center gap-1.5`} data-id="element-2369" data-mp="9uyzdv5f5mu6">
                PK36BOPU0000001234567890
                <CopyIcon className={`w-3.5 h-3.5 ${textSec} shrink-0`} data-id="element-2370" data-mp="irzjhplggyrt" />
              </span>
            </div>
          </div>
        </motion.div>

        {/* Options — content tiles (icon + title + description), not single-action
            CTAs, so kept full-width/auto-height rather than the 55x280 button spec.
            Radius brought down to 8px per the tile-radius rule. */}
        {options.map((opt, i) => <motion.button key={opt.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.08 + i * 0.08,
        type: 'spring',
        stiffness: 300,
        damping: 24
      }} onClick={() => onNavigate(opt.view)} className="w-full text-left rounded-lg p-6 relative overflow-hidden shadow-md" style={{
        background: 'linear-gradient(135deg, #F37024, #D95A1A)'
      }} data-id="element-2371" data-mp="3h2t3rysb2ct5" data-mpi={i}>
            <div className="relative z-10 flex items-center justify-between" data-id="element-2372" data-mp="ina37cbe8muy">
              <div data-id="element-2373" data-mp="ne08kpjtkppn">
                <h3 className="text-white font-normal text-[16px] mb-1" data-id="element-2374" data-mp="nfbkjcnf53ru">
                  {opt.title}
                </h3>
                <p className="text-white/80 font-medium text-[13px]" data-id="element-2375" data-mp="x9qmpde87elx">{opt.desc}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm" data-id="element-2376" data-mp="sbp5ky0d1pd3">
                <opt.icon className="w-6 h-6 text-white" strokeWidth={2} data-id="element-2377" data-mp="ne5lvikw8kdr" />
              </div>
            </div>
            {/* Decorative background circle */}
            <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" data-id="element-2378" data-mp="ecfuuduxbngi" />
          </motion.button>)}
      </div>
    </motion.div>;
}